import { ref, computed } from 'vue'
import { defineStore } from 'pinia'
import { getStorageItemWithExpiry, setStorageItemWithExpiry } from '@/utils/localStorage'
import type { CompleteLoginResponseData } from '@/types/api/auth'
import type { UserData } from '@/types/api/user'

const authToken = getStorageItemWithExpiry<string>('authToken')

export const useAuthStore = defineStore('auth', () => {
  if (!authToken) {
    localStorage.clear()
  }

  const token = ref<string | null>(authToken)
  const tokenType = ref(getStorageItemWithExpiry<string>(`tokenType_${authToken}`))
  const refreshToken = ref(getStorageItemWithExpiry<string>('ref'))
  const user = ref(getStorageItemWithExpiry<UserData>(`user_${authToken}`))

  const isAuth = computed(() => !!token.value)
  // replace with proper class/utility for permissions?
  const isAdmin = computed(() => user.value?.roles?.includes('admin'))

  const clearStore = () => {
    token.value = null
    user.value = null
    localStorage.clear()
  }

  const writeData = (payload: CompleteLoginResponseData) => {
    const {
      token: responseToken,
      expiresIn,
      user: responseUser,
      tokenType: responseTokenType,
    } = payload
    token.value = responseToken.accessToken
    tokenType.value = responseTokenType
    refreshToken.value = responseToken.refreshToken
    user.value = responseUser

    setStorageItemWithExpiry('authToken', responseToken.accessToken, expiresIn * 24 * 7 * 1000)
    setStorageItemWithExpiry('ref', responseToken.refreshToken, expiresIn * 24 * 7 * 1000)
    setStorageItemWithExpiry(`user_${responseToken.accessToken}`, responseUser)
    setStorageItemWithExpiry(`tokenType_${responseToken.accessToken}`, responseTokenType)
  }

  return { token, tokenType, refreshToken, user, isAuth, isAdmin, clearStore, writeData }
})
